import { tagged } from "@mirage/service-logging";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import type { PAPEvent, PAPEventDefaultProperties } from "../events/base/event";
import { DashActionSurface } from "../events/enums/dash_action_surface";
import type { SessionStartReason } from "../events/enums/session_start_reason";
import type { SessionManager } from "./session-manager";
import { SessionManagerName } from "./session-manager";

const logger = tagged("session-aware-events");

export const SESSION_MANAGER_ID_KEYS = {
  [SessionManagerName.DashApp]: "dashSessionId",
  [SessionManagerName.Dash]: "dashSessionId",
  [SessionManagerName.Answers]: "answersSessionId",
  [SessionManagerName.Search]: "searchSessionId",
  [SessionManagerName.SearchAttempt]: "searchAttemptId",
  [SessionManagerName.ExplicitAnswerDwell]: "explicitAnswerDwellId",
  [SessionManagerName.ImplicitAnswerDwell]: "implicitAnswerDwellId",
} as const;

export type SessionAwareness = {
  [K in (typeof SESSION_MANAGER_ID_KEYS)[keyof typeof SESSION_MANAGER_ID_KEYS]]?: string;
};

export type SessionAwareEventCallback = (
  sessionAwareness: SessionAwareness,
) => PAPEvent;

export type SessionAwareEventOptions = {
  shouldExtendSession?: boolean;
  shouldVoidIfNoSession?: boolean;
  startReason?: SessionStartReason;
  defaultProperties?: PAPEventDefaultProperties;
  flush?: boolean;
};

export type ReportSessionAwareEvent = (
  sessionManagerNames: SessionManagerName[],
  callback: SessionAwareEventCallback,
  options?: SessionAwareEventOptions,
) => void;

export type WithActionSurfaceFn = (
  callback: (actionSurfaceResult: {
    dashActionSurface?: DashActionSurface | undefined;
    actionSurface?: DashActionSurface | undefined;
  }) => PAPEvent,
) => PAPEvent;

export async function buildSessionAwareness(
  sessionManagerNames: SessionManagerName[],
  sessionManagerMap: Record<SessionManagerName, SessionManager | undefined>,
  options: SessionAwareEventOptions = {},
): Promise<SessionAwareness> {
  const sessionAwareness: SessionAwareness = {};
  for (const sessionManager of sessionManagerNames
    .map((name) => sessionManagerMap[name])
    .filter(isDefined)) {
    sessionAwareness[SESSION_MANAGER_ID_KEYS[sessionManager.name]] =
      await createSessionAwareness(sessionManager, options);
  }

  return sessionAwareness;
}

export async function createSessionAwareness(
  sessionManager: SessionManager,
  options: SessionAwareEventOptions = {},
) {
  let sessionId = sessionManager.getSession()?.id;

  if (options.shouldVoidIfNoSession && !sessionId) {
    logger.error(
      `Tried to report session-aware event but no session was found for ${sessionManager.name}`,
    );
    return undefined;
  }

  if (!sessionId) {
    sessionId = (
      await sessionManager.extendOrCreateSession(
        options.startReason ?? "new_session",
      )
    ).id;
  } else if (options.shouldExtendSession) {
    sessionId = (await sessionManager.extendOrCreateSession())?.id;
  }

  return sessionId;
}
